import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { Icon } from "@iconify/react";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import getSinglePriceFreeze from "../../controllers/booking/getSinglePriceFreeze";
import approvePriceFreeze from "../../controllers/booking/approvePriceFreeze";
import cancelPriceFreeze from "../../controllers/booking/cancelPriceFreeze";
import { formatMoney } from "../../features/utils/formatMoney";
import Button1 from "../form/Button1";
import usePDF from "../../hooks/flight/usePDF";
import BnplFreezeConfirmationPDFSection from "../pdf/bnplFreezePDF/BnplFreezeConfirmationPDFSection";

const style = {
  position: "absolute",
  top: "50%",
  right: "0%",
  transform: "translate(0%, -50%)",
  width: window.innerWidth > 600 ? "80vw" : "100vw",
  maxWidth: window.innerWidth > 600 ? "560px" : "100vw",
  height: "100vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  outline: "none",
  display: "flex",
  flexDirection: "column",
};

function getFreezeStatusStyles(status) {
  const normalized = String(status || "").toUpperCase();

  switch (normalized) {
    case "INITIATED":
      return {
        label: "Initiated",
        className: "bg-sky-50 text-sky-700 border-sky-200",
        icon: "mdi:snowflake",
      };
    case "INITIALPAID":
      return {
        label: "Initial Paid",
        className: "bg-amber-50 text-amber-700 border-amber-200",
        icon: "mdi:cash-check",
      };
    case "APPROVED":
      return {
        label: "Approved",
        className: "bg-indigo-50 text-indigo-700 border-indigo-200",
        icon: "mdi:check-decagram",
      };
    case "DECLINED":
    case "LOANREJECTED":
      return {
        label: "Declined",
        className: "bg-rose-50 text-rose-700 border-rose-200",
        icon: "mdi:close-circle-outline",
      };
    case "COMPLETED":
    case "PAID":
    case "FULLYPAID":
      return {
        label: "Fully paid",
        className: "bg-emerald-50 text-emerald-700 border-emerald-200",
        icon: "mdi:check-circle",
      };
    default:
      return {
        label: status || "-",
        className: "bg-slate-50 text-slate-700 border-slate-200",
        icon: "mdi:snowflake-alert",
      };
  }
}

function money(amount) {
  if (amount === null || amount === undefined || amount === "") return "-";
  return formatMoney(amount);
}

function formatDate(value) {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : value;
}

function getRouteSummary(flight) {
  const destinations = flight?.destinations || [];
  if (destinations.length) {
    return destinations.map((leg) => `${leg.from} → ${leg.to}`).join(" · ");
  }

  const flights = flight?.flights || [];
  if (!flights.length) return "-";
  const first = flights[0];
  const last = flights[flights.length - 1];
  return `${first?.departureLocation || "?"} → ${last?.arrivalLocation || "?"}`;
}

function getPassengerSummary(travelers = []) {
  if (!travelers.length) return "-";
  const counts = travelers.reduce((acc, traveler) => {
    const type = traveler?.travelerType || "PAX";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([type, count]) => `${count} ${type}`)
    .join(", ");
}

function getPaidInstallmentCount(repayments = [], madeRepayments = [], fullyPaid) {
  if (!repayments.length) return 0;
  if (fullyPaid) return repayments.length;

  let paidPool = madeRepayments.reduce(
    (sum, payment) => sum + Number(payment?.amount || 0),
    0,
  );

  let count = 0;
  for (const installment of repayments) {
    const amount = Number(installment?.amount || 0);
    if (paidPool + 0.01 >= amount) {
      paidPool -= amount;
      count += 1;
    } else {
      break;
    }
  }

  return count;
}

function ShimmerBar({ className = "" }) {
  return <div className={`rounded bg-gray-200 ${className}`} />;
}

function ManageFreezeShimmer() {
  return (
    <div className="flex flex-col gap-5 animate-pulse">
      <ShimmerBar className="h-10 w-36 rounded-full" />
      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <ShimmerBar key={index} className="h-16 w-full" />
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <ShimmerBar className="h-4 w-32" />
        <ShimmerBar className="h-14 w-full" />
        <ShimmerBar className="h-14 w-full" />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="flex flex-col gap-3">
      <h6 className="text-sm font-bold uppercase tracking-wide text-[#6D7C94]">
        {title}
      </h6>
      {children}
    </section>
  );
}

function AmountCard({ label, value, valueClassName = "text-primary" }) {
  return (
    <div className="rounded-lg border border-[#E6E8EC] bg-[#F8FAFC] p-3">
      <div className="text-xs text-[#6D7C94]">{label}</div>
      <div className={`mt-1 text-base font-bold break-all ${valueClassName}`}>
        {value}
      </div>
    </div>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-lg border border-[#E6E8EC] bg-white p-3">
      <div className="text-xs text-[#6D7C94]">{label}</div>
      <div className="mt-1 text-sm font-semibold text-primary break-all">
        {value || "-"}
      </div>
    </div>
  );
}

export default function ManageFreezeOrderModal({ open, setOpen, callback }) {
  const order = typeof open === "object" && open ? open : null;
  const booking = order?.flightBooking?.at?.(0) || order?.bookingData || null;
  const priceFreezeId = booking?.priceFreezeId;

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [payload, setPayload] = useState(null);
  const pdfRef = useRef(null);
  const { handlePDFDownload } = usePDF();

  useEffect(() => {
    if (!open) {
      setPayload(null);
      setLoading(false);
      setActionLoading(false);
      setConfirmAction(null);
      return;
    }

    if (!priceFreezeId) {
      console.log("Manage freeze: missing priceFreezeId", { order, booking });
      return;
    }

    let cancelled = false;

    async function fetchPriceFreeze() {
      setLoading(true);
      const res = await getSinglePriceFreeze(priceFreezeId);
      console.log("single-price-freeze response:", res);

      if (cancelled) return;

      setLoading(false);
      if (res.return) {
        setPayload(res.data);
      } else {
        setPayload(null);
        enqueueSnackbar(res.msg || "Failed to fetch price freeze", {
          variant: "error",
        });
      }
    }

    fetchPriceFreeze();

    return () => {
      cancelled = true;
    };
  }, [open, priceFreezeId]);

  const handleClose = () => setOpen(false);

  const freeze = payload?.priceFreeze;
  const detail = payload?.detail;
  const flight = payload?.flight || booking;
  const status =
    freeze?.status || flight?.priceFreezeStatus || booking?.priceFreezeStatus;
  const statusNormalized = String(status || "").toUpperCase();
  const bookingFreezeStatus =
    flight?.priceFreezeStatus || booking?.priceFreezeStatus || freeze?.status;
  const isInitiated =
    String(bookingFreezeStatus || "").toUpperCase() === "INITIATED";
  const isDeclined =
    ["LOANREJECTED", "DECLINED"].includes(statusNormalized) ||
    ["LOANREJECTED", "DECLINED"].includes(
      String(bookingFreezeStatus || "").toUpperCase(),
    );
  const showCancelCta = isInitiated || isDeclined;
  const statusStyles = getFreezeStatusStyles(status);

  const repayments = freeze?.repayments || [];
  const madeRepayments = freeze?.madeRepayments || [];
  const travelers = flight?.travelers || [];
  const totalInstallments = repayments.length;
  const fullyPaid =
    statusNormalized === "FULLYPAID" ||
    statusNormalized === "COMPLETED" ||
    statusNormalized === "PAID" ||
    (totalInstallments > 0 && Number(detail?.remainingAmount) === 0);

  const paidCount = getPaidInstallmentCount(
    repayments,
    madeRepayments,
    fullyPaid,
  );
  const pendingCount = Math.max(totalInstallments - paidCount, 0);
  const totalMade = madeRepayments.reduce(
    (sum, payment) => sum + Number(payment?.amount || 0),
    0,
  );
  const lastPaymentDate =
    madeRepayments[madeRepayments.length - 1]?.paymentDate || null;

  async function handleFreezeAction(action) {
    const id = priceFreezeId || freeze?._id;
    if (!id) {
      enqueueSnackbar("Price freeze ID is missing", { variant: "error" });
      return;
    }

    setActionLoading(true);
    const res =
      action === "CANCEL"
        ? await cancelPriceFreeze(id)
        : await approvePriceFreeze({
            priceFreezeId: id,
            status: "APPROVED",
          });
    setActionLoading(false);

    if (res.return) {
      enqueueSnackbar(
        res.msg ||
          (action === "CANCEL"
            ? "Price freeze cancelled"
            : "Price freeze approved"),
        { variant: "success" },
      );
      setConfirmAction(null);
      setOpen(false);
      callback?.();
    } else {
      enqueueSnackbar(res.msg || "Failed to update price freeze", {
        variant: "error",
      });
    }
  }

  async function handleDownloadPdf() {
    if (!pdfRef.current) {
      enqueueSnackbar("PDF template is not ready yet.", { variant: "warning" });
      return;
    }
    if (!payload) {
      enqueueSnackbar("Load freeze details before exporting.", {
        variant: "warning",
      });
      return;
    }

    setPdfLoading(true);
    try {
      const string = pdfRef.current.innerHTML;
      await handlePDFDownload(string, {
        fileName:
          freeze?.reference ||
          flight?.bookingId ||
          booking?.bookingId ||
          "bnpl-freeze-order",
      });
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <Modal open={Boolean(open)} onClose={handleClose}>
      <Box sx={style}>
        <div className="flex items-center justify-between gap-3 border-b border-[#E6E8EC] px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <h5 className="text-lg font-bold text-primary">Manage freeze</h5>
            <span className="text-sm text-[#6D7C94]">
              {freeze?.reference ||
                flight?.bookingId ||
                booking?.bookingId ||
                "BNPL order"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={loading || pdfLoading || !payload}
              className="rounded-md p-1 text-[#6D7C94] hover:bg-[#F0F6FC] hover:text-primary disabled:opacity-40"
              aria-label="Download BNPL itinerary PDF"
              title="Download PDF"
            >
              <Icon
                icon={pdfLoading ? "mdi:loading" : "mdi:file-document-outline"}
                className={`text-2xl ${pdfLoading ? "animate-spin" : ""}`}
              />
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1 text-[#6D7C94] hover:bg-[#F0F6FC] hover:text-primary"
              aria-label="Close manage freeze"
            >
              <Icon icon="mdi:close" className="text-2xl" />
            </button>
          </div>
        </div>

        <div className="hidden">
          <div ref={pdfRef}>
            {payload ? (
              <BnplFreezeConfirmationPDFSection data={payload} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
          {loading ? (
            <ManageFreezeShimmer />
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <div
                  className={`inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ${statusStyles.className}`}
                >
                  <Icon icon={statusStyles.icon} className="text-lg" />
                  {statusStyles.label}
                </div>
                {fullyPaid ? (
                  <span className="text-sm font-medium text-emerald-700">
                    Fully paid
                    {flight?.status
                      ? ` · Flight ${flight.status}`
                      : ""}
                  </span>
                ) : null}
              </div>

              <Section title="Order summary">
                <div className="grid grid-cols-2 gap-3">
                  <InfoCard label="Freeze reference" value={freeze?.reference} />
                  <InfoCard label="Booking ID" value={flight?.bookingId} />
                  <InfoCard label="Airline PNR" value={flight?.pnr} />
                  <InfoCard label="Flight status" value={flight?.status} />
                  <InfoCard label="Route" value={getRouteSummary(flight)} />
                  <InfoCard
                    label="Passengers"
                    value={getPassengerSummary(travelers)}
                  />
                </div>
              </Section>

              <Section title="BNPL payment plan">
                <div className="grid grid-cols-2 gap-3">
                  <AmountCard
                    label="Frozen amount"
                    value={money(freeze?.frozenAmount)}
                  />
                  <AmountCard
                    label="First payment"
                    value={money(freeze?.firstPayment)}
                  />
                  <AmountCard
                    label="Loaned / total repayment"
                    value={money(freeze?.loanedAmount)}
                  />
                  <AmountCard
                    label="Service fee"
                    value={money(freeze?.serviceFee)}
                  />
                  <AmountCard
                    label="Plan"
                    value={
                      freeze?.plan != null
                        ? `${freeze.plan} repayment${freeze.plan === 1 ? "" : "s"}`
                        : "-"
                    }
                  />
                  <AmountCard
                    label="Interest / min"
                    value={
                      freeze?.interest != null || freeze?.min != null
                        ? `${freeze?.interest ?? "-"}% · min ${freeze?.min ?? "-"}%`
                        : "-"
                    }
                  />
                </div>
              </Section>

              <Section title="Payment status">
                <div className="grid grid-cols-2 gap-3">
                  <AmountCard
                    label="Already repaid"
                    value={money(detail?.alreadyRepaid ?? totalMade)}
                    valueClassName="text-emerald-700"
                  />
                  {!fullyPaid ? (
                    <AmountCard
                      label="Outstanding"
                      value={money(detail?.remainingAmount)}
                      valueClassName="text-amber-700"
                    />
                  ) : (
                    <AmountCard
                      label="Outstanding"
                      value={money(0)}
                      valueClassName="text-emerald-700"
                    />
                  )}
                  <AmountCard
                    label="Paid installments"
                    value={`${paidCount}/${totalInstallments || 0}`}
                    valueClassName="text-emerald-700"
                  />
                  <AmountCard
                    label="Pending"
                    value={pendingCount}
                    valueClassName="text-amber-700"
                  />
                  {freeze?.paymentCompletedWithinDays != null && fullyPaid ? (
                    <AmountCard
                      label="Completed within"
                      value={`${freeze.paymentCompletedWithinDays} day${
                        freeze.paymentCompletedWithinDays === 1 ? "" : "s"
                      }`}
                      valueClassName="text-primary"
                    />
                  ) : null}
                  {lastPaymentDate ? (
                    <AmountCard
                      label="Last payment"
                      value={formatDate(lastPaymentDate)}
                      valueClassName="text-primary"
                    />
                  ) : null}
                </div>
              </Section>

              <Section title="Repayment schedule">
                {repayments.length ? (
                  <div className="flex flex-col">
                    {repayments.map((item, index) => {
                      const isPaid = index < paidCount;
                      return (
                        <div
                          key={item?._id || index}
                          className="flex items-center justify-between gap-3 border-b border-[#E6E8EC] py-3 text-sm last:border-b-0"
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-primary">
                              Instalment {index + 1}
                            </span>
                            <span className="text-[#6D7C94]">
                              Due {formatDate(item?.paymentDate)}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className="font-semibold text-primary">
                              {money(item?.amount)}
                            </span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                isPaid
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-amber-50 text-amber-700"
                              }`}
                            >
                              {isPaid ? "Paid" : "Pending"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-sm text-[#6D7C94]">
                    No repayments scheduled.
                  </span>
                )}
              </Section>

              <Section title="Made payments">
                {madeRepayments.length ? (
                  <div className="flex flex-col">
                    {madeRepayments.map((payment, index) => (
                      <div
                        key={payment?._id || index}
                        className="flex items-center justify-between gap-3 border-b border-[#E6E8EC] py-3 text-sm last:border-b-0"
                      >
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-primary">
                            Payment {index + 1}
                          </span>
                          <span className="text-[#6D7C94]">
                            {formatDate(payment?.paymentDate)}
                          </span>
                        </div>
                        <span className="font-semibold text-emerald-700">
                          {money(payment?.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 text-sm">
                      <span className="text-[#6D7C94]">Total paid</span>
                      <span className="font-bold text-primary">
                        {money(totalMade)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-[#6D7C94]">
                    No payments made yet.
                  </span>
                )}
              </Section>

              {travelers.length ? (
                <Section title="Passengers">
                  <div className="flex flex-col">
                    {travelers.map((traveler, index) => (
                      <div
                        key={traveler?._id || traveler?.id || index}
                        className="flex items-center justify-between gap-3 border-b border-[#E6E8EC] py-3 text-sm last:border-b-0"
                      >
                        <span className="font-medium capitalize text-primary">
                          {[
                            traveler?.title,
                            traveler?.firstName,
                            traveler?.middleName,
                            traveler?.lastName,
                          ]
                            .filter(Boolean)
                            .join(" ") || "-"}
                        </span>
                        <span className="text-[#6D7C94]">
                          {traveler?.travelerType || "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </Section>
              ) : null}
            </>
          )}
        </div>

        {!loading && showCancelCta ? (
          <div className="border-t border-[#E6E8EC] bg-white px-5 py-4">
            {confirmAction ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-[#6D7C94]">
                  {confirmAction === "CANCEL"
                    ? "Are you sure you want to cancel this price freeze? This cannot be undone."
                    : "Are you sure you want to approve this price freeze? This cannot be undone."}
                </p>
                <div className="flex gap-3">
                  <Button1
                    variant="outlined"
                    className="flex-1"
                    disabled={actionLoading}
                    onClick={() => setConfirmAction(null)}
                  >
                    Back
                  </Button1>
                  <Button1
                    className={`flex-1 ${
                      confirmAction === "CANCEL" ? "!bg-red-500" : ""
                    }`}
                    loading={actionLoading}
                    onClick={() => handleFreezeAction(confirmAction)}
                  >
                    Confirm {confirmAction === "CANCEL" ? "cancel" : "approve"}
                  </Button1>
                </div>
              </div>
            ) : isDeclined ? (
              <Button1
                variant="outlined"
                className="w-full !border-red-500 !text-red-600"
                onClick={() => setConfirmAction("CANCEL")}
              >
                Cancel freeze
              </Button1>
            ) : (
              <div className="flex gap-3">
                <Button1
                  variant="outlined"
                  className="flex-1 !border-red-500 !text-red-600"
                  onClick={() => setConfirmAction("CANCEL")}
                >
                  Cancel freeze
                </Button1>
                <Button1
                  className="flex-1"
                  onClick={() => setConfirmAction("APPROVED")}
                >
                  Approve freeze
                </Button1>
              </div>
            )}
          </div>
        ) : null}
      </Box>
    </Modal>
  );
}
